import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchPublicLegalPage } from '../services/legalPagesService';
import { fetchPublicLegalDocument } from '../services/legalDocumentsService';

const SLUG_META = {
  kvkk: {
    title: 'KVKK Aydınlatma Metni',
    titleEn: 'KVKK Privacy Notice',
    fallback: 'KVKK Aydınlatma Metni',
    source: 'document',
    apiKey: 'kvkk',
    bilingual: true,
  },
  'kullanim-kosullari': {
    title: 'Kullanım Koşulları',
    titleEn: 'Terms of Use',
    fallback: 'Kullanım Koşulları',
    source: 'document',
    apiKey: 'kullanim-kosullari',
    bilingual: true,
  },
  gizlilik: {
    title: 'Gizlilik Politikası',
    titleEn: 'Privacy Policy',
    fallback: 'Gizlilik Politikası',
    source: 'document',
    apiKey: 'privacy',
    bilingual: true,
  },
  privacy: {
    title: 'Gizlilik Politikası',
    titleEn: 'Privacy Policy',
    fallback: 'Gizlilik Politikası',
    source: 'document',
    apiKey: 'privacy',
    bilingual: true,
  },
  'musteri-verileri-taahhut': {
    title: 'Müşteri Verileri Taahhütnamesi',
    titleEn: 'Customer Data Undertaking',
    fallback: 'Müşteri Verileri Taahhütnamesi',
    source: 'document',
    apiKey: 'musteri-verileri-taahhut',
    bilingual: true,
  },
  'acik-riza': {
    title: 'Açık Rıza Beyanı',
    titleEn: 'Explicit Consent',
    fallback: 'Açık Rıza Beyanı',
    source: 'legacy',
    bilingual: false,
  },
};

const FALLBACK_META = {
  title: 'Hukuki Metin',
  titleEn: 'Legal notice',
  fallback: 'Hukuki Metin',
  source: 'legacy',
  bilingual: false,
};

function resolveInitialLang(searchParams) {
  const q = String(searchParams.get('lang') || '').toLowerCase();
  if (q === 'en' || q === 'tr') return q;
  if (typeof navigator !== 'undefined' && /^en\b/i.test(navigator.language || '')) {
    return 'en';
  }
  return 'tr';
}

export default function LegalPublicPage({ slug }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const meta = SLUG_META[slug] || FALLBACK_META;

  const [lang, setLang] = useState(() => resolveInitialLang(searchParams));
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const displayTitle = useMemo(() => {
    if (page?.baslik) return page.baslik;
    return lang === 'en'
      ? meta.titleEn || meta.title || meta.fallback
      : meta.title || meta.fallback;
  }, [page, lang, meta.titleEn, meta.title, meta.fallback]);

  useEffect(() => {
    document.title = `ÜNMAK | ${displayTitle}`;
    return () => {
      document.title = 'SM Yönetim Sistemi';
    };
  }, [displayTitle]);

  useEffect(() => {
    const q = String(searchParams.get('lang') || '').toLowerCase();
    if ((q === 'en' || q === 'tr') && q !== lang) {
      setLang(q);
    }
  }, [searchParams, lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        let json;
        if (meta.source === 'document') {
          const apiKey = meta.apiKey || slug;
          json = await fetchPublicLegalDocument(apiKey, lang);
          if ((!json?.basarili || !json?.veri) && slug === 'kvkk') {
            json = await fetchPublicLegalPage(slug);
          }
        } else {
          json = await fetchPublicLegalPage(slug);
        }
        if (cancelled) return;
        if (!json?.basarili || !json?.veri) {
          setError(json?.mesaj || (lang === 'en' ? 'Page could not be loaded' : 'Sayfa yüklenemedi'));
          return;
        }
        setPage(json.veri);
      } catch {
        if (!cancelled) {
          setError(lang === 'en' ? 'An error occurred while loading the page' : 'Sayfa yüklenirken bir hata oluştu');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, meta.source, meta.apiKey, lang]);

  const switchLang = (next) => {
    if (next !== 'tr' && next !== 'en') return;
    setLang(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('lang', next);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brandRow}>
            <img
              src="/assets/img/logo.png"
              alt="ÜNMAK"
              style={styles.logo}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div style={styles.brandText}>
              <div style={styles.companyName}>
                ÜNLÜSOY ISITMA SİSTEMLERİ SAN. VE TİC. A.Ş.
              </div>
              <div style={styles.companyMeta}>
                {lang === 'en' ? 'UNMAK Service · Legal' : 'ÜNMAK Servis · Yasal metinler'}
              </div>
            </div>
          </div>

          {meta.bilingual ? (
            <div style={styles.langSwitch} role="group" aria-label="Language">
              <button
                type="button"
                onClick={() => switchLang('tr')}
                style={{
                  ...styles.langBtn,
                  ...(lang === 'tr' ? styles.langBtnActive : null),
                }}
              >
                Türkçe
              </button>
              <button
                type="button"
                onClick={() => switchLang('en')}
                style={{
                  ...styles.langBtn,
                  ...(lang === 'en' ? styles.langBtnActive : null),
                }}
              >
                English
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.titleBlock}>
          <h1 style={styles.title}>{displayTitle}</h1>
          {page?.version ? (
            <div style={styles.version}>
              {lang === 'en' ? 'Version' : 'Sürüm'} {page.version}
            </div>
          ) : null}
        </div>

        {loading && (
          <div style={styles.loading}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
            {lang === 'en' ? 'Loading…' : 'Yükleniyor…'}
          </div>
        )}
        {!loading && error && <div style={styles.error}>{error}</div>}
        {!loading && !error && page && (
          <article
            style={styles.article}
            dangerouslySetInnerHTML={{ __html: page.icerik }}
          />
        )}
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  },
  headerInner: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '18px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    minWidth: 0,
    flex: '1 1 280px',
  },
  logo: {
    width: 120,
    height: 48,
    objectFit: 'contain',
    objectPosition: 'left center',
    flexShrink: 0,
  },
  brandText: {
    minWidth: 0,
  },
  companyName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  companyMeta: {
    marginTop: 4,
    fontSize: 12,
    color: '#64748b',
  },
  langSwitch: {
    display: 'inline-flex',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    overflow: 'hidden',
    background: '#f8fafc',
    flexShrink: 0,
  },
  langBtn: {
    border: 0,
    padding: '8px 14px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
    background: 'transparent',
    color: '#334155',
  },
  langBtnActive: {
    background: '#1e5aaa',
    color: '#ffffff',
  },
  main: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '28px 24px 48px',
  },
  titleBlock: {
    marginBottom: 16,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  version: {
    marginTop: 6,
    fontSize: 13,
    color: '#94a3b8',
  },
  loading: {
    textAlign: 'center',
    color: '#64748b',
    padding: '48px 0',
  },
  error: {
    padding: 16,
    borderRadius: 10,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
  },
  article: {
    background: '#ffffff',
    borderRadius: 12,
    padding: '28px 32px',
    boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
    color: '#334155',
    lineHeight: 1.7,
    fontSize: 15,
  },
};
